import React, { Component, ReactNode, useCallback } from 'react';
import { getUpdatedTextWithEmoji } from '../../../../../common/components/EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../../../../../common/components/EmojiPicker/EmojiPicker';
import {
  generatePageStructure,
  nameById,
} from '@mm/state/helpers/filterable-select-helpers';
import FilterableSelect from '../../../../../common/components/FilterableSelect/FilterableSelect';
import DeleteCircleButton from '../../../../../common/buttons/DeleteCircleButton/DeleteCircleButton';
import AttributeContainingTextInput from '../../../../components/AttributeContainingTextInput/AttributeContainingTextInput';
import './TriggerResponseItem.scss';
import '../../../../../common/scss/input.scss';
import PageModel, { PageKind } from '@mm/state/model/PageModel';
import AttributeModel from '@mm/state/model/AttributeModel';
import PageGroupModel from '@mm/state/model/PageGroupModel';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { useViewActions } from '@mm/state/actions/viewActions';
import { PlusCircleIcon } from '@mm/icons';
import { colors } from '../../../beta/styles/colors';
import { Button } from '@mm/components';
import classNames from 'classnames';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { useSelector } from 'react-redux';
import { selectPageGroups } from '@mm/state/selectors/pageSelectors';
import { DEFAULT_GROUP_NAME } from '../../../build/components/PageOverviewPane/PageOverviewPane';

type Props = {
  response: {
    id: number;
    pageId: number;
    responseText: string;
    sortOrder: number;
  };
  onUpdate: (responseEntity: any) => void;
  pages: PageModel[];
  attributes: AttributeModel[];
  canBeDeleted: boolean;
  onDelete: (sortOrder: number) => void;
  pageGroups: PageGroupModel[];
  activeChatbotId: number;
};

type State = {
  responseType: DropdownOption;
  responseText: string;
};

const BOT_REPLY_OPTIONS = [
  { id: 0, name: 'Dialogue' },
  { id: 1, name: 'Text' },
];

class TriggerResponseItem extends Component<
  Props & {
    viewActions: ReturnType<typeof useViewActions>;
    onCreateDialogue: ReturnType<typeof usePageCRUDActions>['create'];
    folderId?: number;
  },
  State
> {
  textInputRef: HTMLInputElement | HTMLTextAreaElement | null = null;

  constructor(props) {
    super(props);
    const {
      response: { pageId, responseText },
    } = this.props;
    this.state = {
      responseType:
        pageId !== null ? BOT_REPLY_OPTIONS[0] : BOT_REPLY_OPTIONS[1],
      responseText,
    };
  }

  onChangeResponseType = (newType: DropdownOption) => {
    const { onUpdate, response, pages } = this.props;
    if (newType === this.state.responseType) return;
    let newResponseText;
    if (newType.name === 'Text') {
      newResponseText = 'Default text';
      onUpdate({
        ...response,
        pageId: null,
        responseText: newResponseText,
      });
    }
    if (newType.name === 'Dialogue') {
      const welcomePage = pages.find(p => p.kind === PageKind.WelcomePage);

      if (welcomePage) {
        const { id: welcomePageId } = welcomePage;
        newResponseText = null;
        onUpdate({
          ...response,
          pageId: welcomePageId,
          responseText: newResponseText,
        });
      }
    }
    this.setState({
      responseText: newResponseText || '',
      responseType: newType,
    });
  };

  onChangeResponsePage = (value: number | string) => {
    const { response, onUpdate } = this.props;
    const newPageId = value;
    onUpdate({
      ...response,
      pageId: newPageId,
      responseText: null,
    });
  };

  onResponseChange = (e: { target: { value: string } }) => {
    const { value } = e.target;
    this.setState({
      responseText: value,
    });
  };

  onResponseBlur = () => {
    const { response } = this.props;
    if (this.state.responseText !== response.responseText) {
      this.props.onUpdate({
        ...response,
        responseText: this.state.responseText,
        pageId: null,
      });
    }
  };

  onDelete = () => {
    const { response, onDelete } = this.props;
    onDelete(response.sortOrder);
  };

  renderControls() {
    if (this.props.canBeDeleted) {
      return <DeleteCircleButton onClick={this.onDelete} />;
    }

    return null;
  }

  updateInsertEmoji = (emoji: string) => {
    const { response } = this.props;
    const responseText = getUpdatedTextWithEmoji(
      this.state.responseText,
      emoji,
      this.textInputRef as HTMLInputElement,
    );

    this.setState({ responseText });

    this.props.onUpdate({
      ...response,
      responseText,
      pageId: null,
    });
  };

  render() {
    const {
      pages,
      pageGroups,
      response,
      attributes,
      activeChatbotId,
      viewActions,
      onCreateDialogue,
      folderId,
    } = this.props;
    const { responseType, responseText } = this.state;
    let element: ReactNode | Element[] | null = null;

    const onViewDialogueClick = (dialogueId: number) => {
      viewActions.showSlideoutMenu('editor', {
        chatbotId: activeChatbotId,
        dialogueId,
      });
    };

    const handleCreateDialogue = async () => {
      const res = await onCreateDialogue({
        botId: activeChatbotId,
        name: `Dialogue ${Date.now()}`,
        pageGroupId: folderId,
      });

      if (res.page) {
        await this.onChangeResponsePage(res.page.id);

        viewActions.showSlideoutMenu('editor', {
          chatbotId: activeChatbotId,
          dialogueId: res.page.id,
          name: res.page.name,
        });
      }
    };

    switch (responseType.name) {
      case 'Text': {
        element = (
          <div className="trigger-response-item__text-input-wrapper">
            <AttributeContainingTextInput
              className="trigger-response-item__text-input"
              value={responseText || ''}
              onChange={this.onResponseChange}
              onBlur={this.onResponseBlur}
              inputRef={ref => {
                this.textInputRef = ref;
              }}
              attributes={attributes}
            />

            <EmojiPicker
              className="trigger-response-item__emoji-picker"
              onEmojiPicked={this.updateInsertEmoji}
            />
          </div>
        );
        break;
      }
      case 'Dialogue': {
        element = (
          <FilterableSelect
            outerValue={nameById(response.pageId, pages)}
            onChange={this.onChangeResponsePage}
            suggestions={generatePageStructure(pages, pageGroups)}
            renderSuggestionsContainer={({
              containerProps,
              children,
              query,
            }) => {
              return (
                <div
                  {...containerProps}
                  className={classNames(
                    'trigger-response-item__create-dialogue',
                    containerProps?.className,
                  )}
                >
                  <Button
                    color="secondary-blue"
                    css={{
                      container: 'trigger-response-item__create-dialogue-btn',
                    }}
                    onClick={handleCreateDialogue}
                  >
                    <PlusCircleIcon color={colors.accentBlue} />
                    Add Dialogue
                  </Button>
                  {children}
                </div>
              );
            }}
          />
        );
        break;
      }
      default:
        element = <div />;
    }

    return (
      <div className="trigger-response-item">
        <div className="trigger-response-item__heading">
          Bot replies with:
          <div className="trigger-response-item__controls-container">
            {responseType.name === 'Dialogue' && (
              <div
                className="trigger-catch-all__dialogue-view"
                onClick={() => onViewDialogueClick(response.pageId)}
              >
                <i className="far fa-eye" /> &nbsp; View/Edit Dialogue
              </div>
            )}
            {this.renderControls()}
          </div>
        </div>

        <div className="trigger-response-item__options">
          <div className="trigger-response-item__option-select trigger-response-item__input chatbot__input-group">
            <PrimarySelect
              activeItemClassName="trigger-response-item__primary-select-dropdown"
              value={responseType}
              onChange={this.onChangeResponseType}
              options={BOT_REPLY_OPTIONS}
              customDropdownStyles={{ zIndex: '1' }}
            />
          </div>
          <div className="trigger-response-item__option-value trigger-response-item__input chatbot__input-group">
            {element}
          </div>
        </div>
      </div>
    );
  }
}

export default (props: Props) => {
  const viewActions = useViewActions();
  const pageCRUDActions = usePageCRUDActions();
  const dialogueFolders = useSelector(selectPageGroups);

  const folder = dialogueFolders?.find?.(v => v.name === DEFAULT_GROUP_NAME);

  return (
    <TriggerResponseItem
      {...props}
      folderId={folder?.id}
      viewActions={viewActions}
      onCreateDialogue={pageCRUDActions.create}
    />
  );
};
